import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqItem } from '../../../models/faq.model';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CardData {
  icon: string;
  title: string;
  content: string;
}

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}
@Component({
  selector: 'app-mobile-app-development',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './mobile-app-development.component.html',
  styleUrl: './mobile-app-development.component.scss'
})
export class MobileAppDevelopmentComponent implements AfterViewInit, OnDestroy, OnInit {
  private jsonLdScript: any;
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;

  isFeaturesVisible = false;
  isWhyChooseVisible = false;

  private observer: IntersectionObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal
  ) { }
  goToCaseStudies(): void {

  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }

  mbldevFaqs: FaqItem[] = [
    {
      question: 'What types of mobile apps do you build?',
      answer: `We build native Android & iOS apps, cross-platform apps, enterprise mobile solutions, 
      and consumer-facing apps across industries like Fintech, Healthcare, E-commerce, and Hi-tech.`
    },
    {
      question: 'How much does custom mobile app development cost?',
      answer: `Costs vary based on complexity, platforms, integrations, and ongoing support. 
      We’ll scope your project during the discovery phase and provide a transparent quote for your 
      specific requirements.`
    },
    {
      question: 'Can you handle both Android and iOS development?',
      answer: `Yes — our mobile app development company has dedicated teams for Android and iOS, 
      and we also excel in cross-platform solutions when you need faster time-to-market and 
      cost-efficiency.`
    },
    {
      question: 'Will I own the source code and IP?',
      answer: `Absolutely. Once the project is completed and full payment is made, you will own the 
      source code, design assets, and intellectual property rights.`
    },
    {
      question: 'How do you ensure app quality and performance?',
      answer: `We follow agile development best practices, perform extensive QA including unit tests, 
      integration tests, user acceptance tests, and constantly monitor performance metrics. 
      As part of our global delivery model, we emphasize reliability and scalability.`
    },
    {
      question: 'What kind of post-launch support do you provide?',
      answer: `We provide maintenance, updates, new feature enhancements, analytics and performance 
      monitoring. You can choose a support package that suits your needs and budget.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Accelerating Retirement Planning with Intelligent Data Automation',
      description: 'A leading financial advisory platform transformed its manual plan fee analysis into an automated, real-time process. By integrating OCR-based data extraction and Azure-powered benchmarking, the new app empowered over 200 advisors to generate reports in under 5 minutes, achieving 88% faster processing and reducing data errors by 75%.',
      image: '../../../../assets/services/mobile-dev/successStories/Accelerating-Retirement-Planning-with-Intelligent-Data-Automation.png'
    },
    {
      id: 2,
      year: '2023',
      title: 'Empowering Field Sales Teams with an Offline-First Mobile Solution',
      description: 'A global IoT provider enhanced its sales operations with a cross-platform field sales app built using React Native. Equipped with offline data sync, digital quotes, and real-time sales dashboards, the solution improved lead conversion by 30%, shortened deal cycles by 13 days, and boosted sales productivity by $500K in just six months.',
      image: '../../../../assets/services/mobile-dev/successStories/Empowering-Field-Sales-Teams-with-an-Offline-First-Mobile-Solution.png'
    },
    {
      id: 3,
      year: '2025',
      title: 'Boosting SME Growth Through White-Label E-Commerce Apps',
      description: 'Small and medium retailers gained independence from high marketplace fees with a customizable Flutter-based e-commerce app. Featuring integrated payments, push notifications, and AI-driven recommendations, the platform enabled 25+ businesses to launch branded storefronts within months. The app achieved a 50% add-to-cart rate and a 6% conversion rate, outperforming the industry average of 3–4% and helping partners build stronger direct-to-consumer engagement.',
      image: '../../../../assets/services/mobile-dev/successStories/Boosting-SME-Growth-Through-White-Label-E-Commerce-Apps.png'
    },
    {
      id: 4,
      year: '2026',
      title: 'Revolutionizing Skincare Retail with AI & AR-Powered Personalization',
      description: 'A leading skincare brand leveraged AI, ML, and AR technologies to deliver personalized product experiences. With real-time skin analysis, virtual try-ons, and smart reorder features, the app achieved 50K+ downloads, a 65% drop in product returns, and a 25% boost in average basket value, driving $750K in mobile revenue.',
      image: '../../../../assets/services/mobile-dev/successStories/Revolutionizing-Skincare-Retail-with-AI-AR-Powered-Personalization.png'
    }
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 1; // Start with middle card selected

  // THIS IS THE FIX: All instances of 'this.cards' are changed to 'this.timelinecards'
  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];

    // Top card
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card (selected)
    result.push({
      card: this.timelinecards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card
    if (this.selectedIndex < this.timelinecards.length - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  // This is the other array that caused the conflict. It is now distinct.
  cards: CardData[] = [
    {
      icon: '../../assets/services/software-dev/angularjs-line.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
    {
      icon: '../../assets/services/software-dev/puzzle.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
    {
      icon: '../../assets/services/software-dev/focus.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
    {
      icon: '../../assets/services/software-dev/dossier.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
    {
      icon: '../../assets/services/software-dev/shapes.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
    {
      icon: '../../assets/services/software-dev/brush.svg',
      title: 'Condimentum Tortor, Eget Lacinia',
      content: `Praesent Magna Mi, Tempor Vel Libero Eget, Pulvinar Fringilla Elit.
  Donec Ut Mattis Lacus, Vel Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit
  Dignissim Massa. Vivamus Efficitur, Erat Sit Amet Dapibus Bibendum, Orci Elit`,
    },
  ];

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/mobile-dev/whyChooseUs/ProvenTrackRecord.png',
      title: 'Proven Track Record',
      description: 'Delivering innovative mobile solutions for startups and enterprises across diverse domains, including Insurtech, Fintech, and Healthcare, driving measurable business impact.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/whyChooseUs/GlobalDeliveryPresence.png',
      title: 'Global Delivery Presence',
      description: 'Headquartered in Bengaluru with a strong presence in the USA, ensuring seamless collaboration, communication, and on-time delivery across markets.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/whyChooseUs/DedicatedMobileAppExperts.png',
      title: 'Dedicated Mobile App Experts',
      description: 'Access senior mobile app developers skilled in Android, iOS, React Native, and Flutter, delivering enterprise-grade architecture and robust performance.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/whyChooseUs/Cost-EfficiencyFlexibility.png',
      title: 'Cost-Efficiency & Flexibility',
      description: 'Benefit from competitive pricing and flexible engagement models, combining India-based expertise with a global delivery mindset for maximum value.'
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/mobile-dev/developmentProcess/DiscoveryStrategy.png',
      title: 'Discovery & Strategy',
      description: 'We begin with a deep dive: business goals, user personas, market analysis (USA & India), and platform selection (Android, iOS, or cross-platform).'
    },
    {
      icon: '../../../../assets/services/mobile-dev/developmentProcess/DesignPrototyping.png',
      title: 'Design & Prototyping',
      description: 'UI/UX mockups, clickable prototypes, and review loops to ensure the look & feel aligns with your vision and user-behavior.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/developmentProcess/DevelopmentTesting.png',
      title: 'Development & Testing',
      description: 'Our mobile application developers build your app with best-in-class code, conduct iterative testing (unit, integration, user acceptance) and ensure optimal performance on Android and iOS.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/developmentProcess/LaunchDeployment.png',
      title: 'Launch & Deployment',
      description: 'We manage app store submission, compliance, and go-live for both Android Play Store and Apple App Store.'
    },
    {
      icon: '../../../../assets/services/mobile-dev/developmentProcess/Post-LaunchSupportGrowth.png',
      title: 'Post-Launch Support & Growth',
      description: 'Maintenance, updates, analytics tracking, feature enhancements, designed to keep your app competitive and scalable.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }
  setupTiltEffect(): void {
    // Target both feature cards and the feature image
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;

        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Switch to fast transition for smooth tilt effect on hover
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  injectSchemaMarkup(): void {
    const mobileAppSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/mobile-app-development#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/mobile-app-development",
          "name": "Mobile App Development Services | Android, iOS & Cross-Platform | Kalpita Technologies",
          "description": "End-to-end mobile app development services including native Android, iOS, and cross-platform (Flutter, React Native) solutions.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/mobile-app-development#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "Mobile App Development", "item": "https://www.kalpitatechnologies.com/company-services/mobile-app-development" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/mobile-app-development#service",
          "name": "Mobile App Development Services",
          "serviceType": "Mobile App Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end mobile app development services including custom Android, iOS, and cross-platform (Flutter, React Native) solutions."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/mobile-app-development#process",
          "name": "Our Proven 5-Step Mobile App Development Process",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discovery & Strategy", "text": "Deep dive into business goals, user personas, and platform selection." },
            { "@type": "HowToStep", "position": 2, "name": "Design & Prototyping", "text": "UI/UX mockups and review loops to ensure the look and feel aligns with your vision." },
            { "@type": "HowToStep", "position": 3, "name": "Development & Testing", "text": "Build with best-in-class code and conduct iterative testing." },
            { "@type": "HowToStep", "position": 4, "name": "Launch & Deployment", "text": "Manage app store submission and go-live compliance." },
            { "@type": "HowToStep", "position": 5, "name": "Post-Launch Support", "text": "Maintenance, updates, and feature enhancements for scalability." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/mobile-app-development#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What types of mobile apps do you build?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We build native Android and iOS apps, cross-platform apps using Flutter and React Native, and enterprise solutions."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(mobileAppSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
